import { useState, useRef } from "react";
import { Button, FAQAccordion, IgIcon, Seo } from "../../components";
import { MapPin, Phone, Mail, Clock, Send, Share2, ExternalLink } from "lucide-react";
import "./KontaktPage.scss";
import { CaptchaWidget } from "../../components/CaptchaWidget/CaptchaWidget";
import { CONTACT_URL } from "../../features/UrlHelper";
import { handleApiError } from "../../features/errorHandler";
import axios from "axios";

function KontaktPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [captchaToken, setCaptchaToken] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const captchaRef = useRef(null);
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      alert("Prosím, potvrďte, že nie ste robot.");
      return;
    }

    setStatus("sending");
    setErrorMessage("");

    try {
      const { data } = await axios.post(CONTACT_URL, {
        ...form,
        cfTurnstileToken: captchaToken,
      });

      if (data.success) {
        setStatus("success");
        setForm({ name: "", email: "", subject: "", message: "" });
        setCaptchaToken(null);
      } else {
        setCaptchaToken(null);
        captchaRef.current.reset();
        setStatus("error");
        setErrorMessage(
          data.message || "Odoslanie správy zlyhalo. Skúste to znova.",
        );
      }
    } catch (error) {
      setStatus("error");
      const msg = handleApiError(error, "Chyba pripojenia. Skúste to znova.");
      setErrorMessage(msg);
    }
  };

  return (
    <div className="kontakt">
      <Seo
        title="Kontakt"
        description="Ozvite sa nám! Kontaktujte školu Capoeira Bratislava cez formulár, e-mail alebo telefón. Téšíme sa na vás."
        canonical="/kontakt"
      />
      <section className="kontakt__hero">
        <div className="kontakt__hero-container">
          <span className="kontakt__hero-tag">Kontakt</span>
          <h1 className="kontakt__hero-title">Ozvite sa nám</h1>
        </div>
      </section>

      <section className="kontakt__main">
        <div className="kontakt__container">
          {/* 1. Horná časť: Veľký obrázok + Kontaktný formulár */}
          <div className="kontakt__top-grid">
            <div className="kontakt__image-showcase">
              <div className="kontakt__image-glow" />
              <img
                src="/pictures/0-02-05-12d6a15bab85740f4e2e04629e009f8ea78ea8a1f04f91ad1cfd868f78eff2b1_fe62e7959a1f2fcf.PNG"
                alt="Professor Fogo - Capoeira Bratislava"
                className="kontakt__large-image"
              />
            </div>

            <div className="kontakt__form-wrapper">
              <h2 className="kontakt__form-title">Napíšte nám</h2>

              {status === "success" ? (
                <div className="kontakt__success">
                  <div className="kontakt__success-icon">✓</div>
                  <h3>Správa odoslaná!</h3>
                  <p>Ďakujeme za vašu správu. Ozveme sa vám čo najskôr.</p>
                  <Button variant="ghost" onClick={() => setStatus("idle")}>
                    Odoslať ďalšiu správu
                  </Button>
                </div>
              ) : (
                <form className="kontakt__form" onSubmit={handleSubmit}>
                  <div className="kontakt__form-row">
                    <div className="kontakt__form-field">
                      <label className="kontakt__form-label">Meno</label>
                      <input
                        className="kontakt__form-input"
                        value={form.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                        placeholder="Vaše meno"
                        required
                      />
                    </div>
                    <div className="kontakt__form-field">
                      <label className="kontakt__form-label">Email</label>
                      <input
                        type="email"
                        className="kontakt__form-input"
                        value={form.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="vas@email.com"
                        required
                      />
                    </div>
                  </div>
                  <div className="kontakt__form-field">
                    <label className="kontakt__form-label">Predmet</label>
                    <input
                      className="kontakt__form-input"
                      value={form.subject}
                      onChange={(e) => handleChange("subject", e.target.value)}
                      placeholder="O čom chcete písať?"
                      required
                    />
                  </div>
                  <div className="kontakt__form-field">
                    <label className="kontakt__form-label">Správa</label>
                    <textarea
                      className="kontakt__form-textarea"
                      value={form.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      placeholder="Vaša správa..."
                      rows={5}
                      required
                    />
                  </div>
                  <CaptchaWidget ref={captchaRef} onVerify={(token) => setCaptchaToken(token)} />
                  {status === "error" && (
                    <p className="kontakt__form-error">{errorMessage}</p>
                  )}
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={status === "sending"}
                  >
                    <Send size={16} />
                    {status === "sending" ? "Odosiela sa..." : "Odoslať správu"}
                  </Button>
                </form>
              )}
            </div>
          </div>

          {/* 2. Kontaktné údaje na celú šírku - Bento Layout */}
          <div className="kontakt__info-section">
            <div className="kontakt__section-header">
              <span className="kontakt__section-tag">Kontaktné informácie</span>
              <h2 className="kontakt__section-title">Všetko na jednom mieste</h2>
              <p className="kontakt__section-subtitle">
                Zastavte sa na tréningu, zavolajte nám alebo nám napíšte kedykoľvek.
              </p>
            </div>

            <div className="kontakt__bento-grid">
              {/* Karta 1: Adresa */}
              <div className="kontakt__bento-card">
                <div className="kontakt__bento-top">
                  <div className="kontakt__bento-icon kontakt__bento-icon--gold">
                    <MapPin size={22} />
                  </div>
                  <span className="kontakt__bento-tag">Lokalita</span>
                </div>
                <div className="kontakt__bento-body">
                  <h3 className="kontakt__bento-title">Kde trénujeme</h3>
                  <p className="kontakt__bento-text">
                    Spojená škola Novohradská (GJH)<br />
                    Novohradská 3, 821 09 Bratislava
                  </p>
                </div>
                <div className="kontakt__bento-footer">
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=Spojen%C3%A1+%C5%A1kola+Novohradsk%C3%A1,+Novohradsk%C3%A1+3,+821+09+Bratislava"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="kontakt__bento-action"
                  >
                    Navigovať na mape <ExternalLink size={14} />
                  </a>
                </div>
              </div>

              {/* Karta 2: Tréningové hodiny */}
              <div className="kontakt__bento-card">
                <div className="kontakt__bento-top">
                  <div className="kontakt__bento-icon kontakt__bento-icon--gold">
                    <Clock size={22} />
                  </div>
                  <span className="kontakt__bento-tag">Rozvrh</span>
                </div>
                <div className="kontakt__bento-body">
                  <h3 className="kontakt__bento-title">Tréningové hodiny</h3>
                  <div className="kontakt__bento-schedule">
                    <div className="kontakt__schedule-row">
                      <span className="kontakt__schedule-day">Pondelok:</span>
                      <span className="kontakt__schedule-time">16:00 – 18:00</span>
                    </div>
                    <div className="kontakt__schedule-row">
                      <span className="kontakt__schedule-day">Streda:</span>
                      <span className="kontakt__schedule-time">16:00 – 18:00</span>
                    </div>
                  </div>
                </div>
                <div className="kontakt__bento-footer">
                  <span className="kontakt__bento-badge">Prvý tréning zdarma</span>
                </div>
              </div>

              {/* Karta 3: Priamy kontakt */}
              <div className="kontakt__bento-card">
                <div className="kontakt__bento-top">
                  <div className="kontakt__bento-icon kontakt__bento-icon--gold">
                    <Phone size={22} />
                  </div>
                  <span className="kontakt__bento-tag">Priamy kontakt</span>
                </div>
                <div className="kontakt__bento-body">
                  <h3 className="kontakt__bento-title">Telefón & Email</h3>
                  <div className="kontakt__bento-contacts">
                    <a href="tel:+421903664688" className="kontakt__bento-contact-link">
                      <Phone size={15} /> +421 903 664 688
                    </a>
                    <a href="mailto:capoeira@capoeira.sk" className="kontakt__bento-contact-link">
                      <Mail size={15} /> capoeira@capoeira.sk
                    </a>
                  </div>
                </div>
                <div className="kontakt__bento-footer">
                  <span className="kontakt__bento-subtext">Odpovedáme do 24 hodín</span>
                </div>
              </div>

              {/* Karta 4: Sociálne siete */}
              <div className="kontakt__bento-card">
                <div className="kontakt__bento-top">
                  <div className="kontakt__bento-icon kontakt__bento-icon--gold">
                    <Share2 size={22} />
                  </div>
                  <span className="kontakt__bento-tag">Sociálne siete</span>
                </div>
                <div className="kontakt__bento-body">
                  <h3 className="kontakt__bento-title">Sledujte náš klub</h3>
                  <div className="kontakt__bento-socials">
                    <a
                      href="https://www.instagram.com/abada_capoeira_slovensko/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="kontakt__social-pill"
                    >
                      Instagram <ExternalLink size={13} />
                    </a>
                    <a
                      href="https://www.facebook.com/AbadaCapoeiraSlovakia/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="kontakt__social-pill"
                    >
                      Facebook <ExternalLink size={13} />
                    </a>
                  </div>
                </div>
                <div className="kontakt__bento-footer">
                  <span className="kontakt__bento-subtext">Fotky a videá z tréningov</span>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Mapa na celú šírku */}
          <div className="kontakt__map-wrapper">
            <h2 className="kontakt__section-title">Kde nás nájdete</h2>
            <div className="kontakt__map">
              <iframe
                title="Mapa tréningov Capoeira Bratislava"
                src="https://maps.google.com/maps?q=Spojen%C3%A1+%C5%A1kola+Novohradsk%C3%A1,+Novohradsk%C3%A1+3,+821+09+Bratislava&t=&z=16&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="380"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion
        tag="CHCEM ZAČAŤ"
        title="Čo potrebuješ vedieť pred prvým tréningom:"
        items={[
          {
            question: "Prvý tréning je naozaj zdarma?",
            answer:
              "Áno. Prvý tréning je zdarma, aby ste si Capoeira vedeli nezáväzne vyskúšať.",
          },
          {
            question: "Potrebujem mať kondíciu alebo skúsenosti?",
            answer:
              "Nie. Nepotrebuješ byť ohybný ani mať kondíciu. Všetko ťa naučíme od nuly, stačí mať chuť cvičiť.",
          },
          {
            question: "Čo si mám obliecť na prvý tréning?",
            answer:
              "Stačí pohodlné športové oblečenie, ideálne biele tričko a tepláky. Trénujeme naboso.",
          },
        ]}
      />
    </div>
  );
}

export default KontaktPage;
